import axios, { AxiosError, AxiosRequestConfig } from 'axios';
import { 
  LoginRequest, 
  AuthResponse, 
  Enquiry, 
  MembershipPlan, 
  MembersMembership,
  API_BASE_URL 
} from '../types/api';

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'Cache-Control': 'no-cache, no-store, must-revalidate',
    'Pragma': 'no-cache',
    'Expires': '0'
  },
});

// Retry configuration
const MAX_RETRY_ATTEMPTS = 5;
const INITIAL_RETRY_DELAY = 1000; // 1 second
const MAX_RETRY_DELAY = 15000; // 15 seconds

// Store for tracking retry attempts
interface RetryConfig extends AxiosRequestConfig {
  __retryCount?: number;
}

// Function to calculate exponential backoff delay
const getRetryDelay = (retryCount: number): number => {
  const delay = INITIAL_RETRY_DELAY * Math.pow(1.5, retryCount);
  return Math.min(delay, MAX_RETRY_DELAY);
};

// Function to check if error is retryable (database connection issues)
const isRetryableError = (error: AxiosError): boolean => {
  // Retry on network errors
  if (!error.response) {
    return true;
  }

  // Retry on server errors (5xx)
  if (error.response.status >= 500) {
    return true;
  }

  // Retry on specific database connection errors
  const errorData = error.response?.data as any;
  const errorMessage = errorData?.message || errorData?.title || '';
  const retryableMessages = [
    'database',
    'connection',
    'timeout',
    'unable to connect',
    'server is unavailable',
    'service unavailable',
    'cannot open database'
  ];

  return retryableMessages.some(msg => 
    errorMessage.toLowerCase().includes(msg)
  );
};

// Add token to requests
api.interceptors.request.use((config) => {
  const token = sessionStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  
  // Add selected gym ID for Super Admin context (both header and query param)
  const selectedGymId = localStorage.getItem('superadmin_selected_gym');
  if (selectedGymId) {
    config.headers['X-SuperAdmin-SelectedGym'] = selectedGymId;
    // Also add as query parameter for backend compatibility
    if (config.params) {
      config.params.gymId = selectedGymId;
    } else {
      config.params = { gymId: selectedGymId };
    }
  }
  
  return config;
});

// Add response interceptor with automatic retry logic
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const config = error.config as RetryConfig;

    // Don't retry if no config or max retries reached
    if (!config || (config.__retryCount && config.__retryCount >= MAX_RETRY_ATTEMPTS)) {
      return Promise.reject(error);
    }

    // Don't retry if error is not retryable
    if (!isRetryableError(error)) {
      return Promise.reject(error);
    }

    // Initialize retry count
    config.__retryCount = config.__retryCount || 0;
    config.__retryCount++;

    // Calculate delay
    const delay = getRetryDelay(config.__retryCount);

    // Dispatch custom event to notify UI about retry
    window.dispatchEvent(new CustomEvent('apiRetrying', {
      detail: {
        attempt: config.__retryCount,
        maxAttempts: MAX_RETRY_ATTEMPTS,
        delay: delay,
        url: config.url
      }
    }));

    // Wait for delay
    await new Promise(resolve => setTimeout(resolve, delay));

    // Retry the request
    try {
      const response = await api.request(config);
      
      // Dispatch success event
      if (config.__retryCount > 1) {
        window.dispatchEvent(new CustomEvent('apiRetrySuccess', {
          detail: {
            attempts: config.__retryCount,
            url: config.url
          }
        }));
      }
      
      return response;
    } catch (retryError) {
      // If last attempt failed, dispatch failure event
      if (config.__retryCount >= MAX_RETRY_ATTEMPTS) {
        window.dispatchEvent(new CustomEvent('apiRetryFailed', {
          detail: {
            attempts: config.__retryCount,
            url: config.url
          }
        }));
      }
      return Promise.reject(retryError);
    }
  }
);

// Auth API
export const authAPI = {
  login: async (data: LoginRequest): Promise<AuthResponse> => {
    const response = await api.post('/Auth/Login', data);
    return response.data;
  },

  logout: async (): Promise<void> => {
    await api.post('/Auth/Logout');
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('user');
  },

  refreshToken: async (): Promise<AuthResponse> => {
    const response = await api.post('/Auth/RefreshToken');
    return response.data;
  },

  verifyOtp: async (data: { email: string; otp: string; password: string }): Promise<AuthResponse> => {
    const response = await api.post('/Auth/VerifyOtp', data);
    return response.data;
  },

  forgotPassword: async (email: string): Promise<any> => {
    const response = await api.post('/Auth/ForgotPassword', { email });
    return response.data;
  },

  resetPasswordWithOtp: async (data: { email: string; otp: string; newPassword: string }): Promise<any> => {
    const response = await api.post('/Auth/ResetPasswordWithOtp', data);
    return response.data;
  },

  resendOtp: async (email: string): Promise<any> => {
    const response = await api.post('/Auth/ResendOtp', { email });
    return response.data;
  },
};

// Enquiry API
export const enquiryAPI = {
  getAll: async (): Promise<Enquiry[]> => {
    const response = await api.get('/Enquiry');
    return response.data;
  },

  getOpen: async (): Promise<Enquiry[]> => {
    const response = await api.get('/Enquiry/Open');
    return response.data;
  },

  getClosed: async (): Promise<Enquiry[]> => {
    const response = await api.get('/Enquiry/Closed');
    return response.data;
  },

  getById: async (id: number): Promise<Enquiry> => {
    const response = await api.get(`/Enquiry/${id}`);
    return response.data;
  },

  create: async (data: Partial<Enquiry>): Promise<Enquiry> => {
    const response = await api.post('/Enquiry', data);
    return response.data;
  },

  update: async (id: number, data: Partial<Enquiry>): Promise<void> => {
    await api.put(`/Enquiry/${id}`, data);
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/Enquiry/${id}`);
  },

  convertToMember: async (
    id: number, 
    membershipPlanId: number, 
    paidAmount: number,
    discount: number,
    startDate: string,
    paymentMode: string,
    transactionId: string,
    expectedPaymentDate: string,
    createdBy: string
  ): Promise<MembersMembership> => {
    const response = await api.post(`/Enquiry/${id}/ConvertToMember`, {
      membershipPlanId,
      paidAmount,
      discount,
      startDate: startDate ? new Date(startDate).toISOString() : null,
      paymentMode,
      transactionId: transactionId || null,
      expectedPaymentDate: expectedPaymentDate ? new Date(expectedPaymentDate).toISOString() : null,
      createdBy
    });
    return response.data;
  },

  exportToExcel: async (): Promise<Blob> => {
    const response = await api.get('/Enquiry/ExportToExcel', {
      responseType: 'blob'
    });
    return response.data;
  },

  getHistory: async (id: number): Promise<any[]> => {
    const response = await api.get(`/Enquiry/${id}/History`);
    return response.data;
  },
};

// Membership Plan API
export const membershipPlanAPI = {
  getAll: async (): Promise<MembershipPlan[]> => {
    const response = await api.get('/MembershipPlan');
    return response.data;
  },

  getActive: async (): Promise<MembershipPlan[]> => {
    const response = await api.get('/MembershipPlan/Active');
    return response.data;
  },

  getById: async (id: number): Promise<MembershipPlan> => {
    const response = await api.get(`/MembershipPlan/${id}`);
    return response.data;
  },

  create: async (data: Partial<MembershipPlan>): Promise<MembershipPlan> => {
    const response = await api.post('/MembershipPlan', data);
    return response.data;
  },

  update: async (id: number, data: Partial<MembershipPlan>): Promise<void> => {
    await api.put(`/MembershipPlan/${id}`, data);
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/MembershipPlan/${id}`);
  },

  getByType: async (planType: string): Promise<MembershipPlan[]> => {
    const response = await api.get(`/MembershipPlan/ByType/${planType}`);
    return response.data;
  },

  activate: async (id: number): Promise<void> => {
    await api.put(`/MembershipPlan/${id}/Activate`);
  },

  getMembers: async (id: number): Promise<any[]> => {
    const response = await api.get(`/MembershipPlan/${id}/Members`);
    return response.data;
  },

  getStats: async (): Promise<any> => {
    const response = await api.get('/MembershipPlan/Stats');
    return response.data;
  },
};

// Members Membership API
export const membersMembershipAPI = {
  getAll: async (): Promise<MembersMembership[]> => {
    const response = await api.get('/MembersMembership');
    return response.data;
  },

  getActive: async (): Promise<MembersMembership[]> => {
    const response = await api.get('/MembersMembership/Active');
    return response.data;
  },

  getById: async (id: number): Promise<MembersMembership> => {
    const response = await api.get(`/MembersMembership/${id}`);
    return response.data;
  },

  create: async (data: Partial<MembersMembership>): Promise<MembersMembership> => {
    const response = await api.post('/MembersMembership', data);
    return response.data;
  },

  processPayment: async (
    id: number, 
    amount: number,
    discount: number,
    paymentMethod: string,
    transactionId: string,
    expectedDate: string,
    notes: string
  ): Promise<any> => {
    const response = await api.post(`/MembersMembership/${id}/Payment`, {
      amount,
      discount,
      paymentMethod,
      transactionId: transactionId || null,
      expectedDate: expectedDate ? new Date(expectedDate).toISOString() : null,
      notes: notes || ''
    });
    return response.data;
  },

  exportToExcel: async (): Promise<Blob> => {
    const response = await api.get('/MembersMembership/ExportToExcel', {
      responseType: 'blob'
    });
    return response.data;
  },

  upgrade: async (id: number, newMembershipPlanId: number, paidAmount: number, updatedBy: string): Promise<any> => {
    const response = await api.put(`/MembersMembership/${id}/Upgrade`, {
      newMembershipPlanId,
      paidAmount,
      updatedBy
    });
    return response.data;
  },

  toggleStatus: async (id: number, isInactive: boolean, updatedBy: string): Promise<any> => {
    const response = await api.put(`/MembersMembership/${id}/ToggleStatus`, {
      isInactive,
      updatedBy
    });
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/MembersMembership/${id}`);
  },

  getExpired: async (): Promise<MembersMembership[]> => {
    const response = await api.get('/MembersMembership/Expired');
    return response.data;
  },

  getExpiringSoon: async (): Promise<MembersMembership[]> => {
    const response = await api.get('/MembersMembership/ExpiringSoon');
    return response.data;
  },

  getPendingPayments: async (): Promise<MembersMembership[]> => {
    const response = await api.get('/MembersMembership/PendingPayments');
    return response.data;
  },

  getPastMembers: async (): Promise<MembersMembership[]> => {
    const response = await api.get('/MembersMembership/Expired');
    return response.data;
  },

  updateStartDate: async (id: number, newStartDate: string): Promise<any> => {
    const response = await api.put(`/MembersMembership/${id}/UpdateStartDate`, {
      newStartDate
    });
    return response.data;
  },

  renew: async (id: number, data: any): Promise<MembersMembership> => {
    const response = await api.post(`/MembersMembership/${id}/Renew`, data);
    return response.data;
  },

  getStats: async (): Promise<any> => {
    const response = await api.get('/MembersMembership/Stats');
    return response.data;
  },

  getByEnquiry: async (enquiryId: number): Promise<any[]> => {
    const response = await api.get(`/MembersMembership/ByEnquiry/${enquiryId}`);
    return response.data;
  },
};

// User API
export const userAPI = {
  getAll: async (): Promise<any[]> => {
    const response = await api.get('/User');
    return response.data;
  },

  getAvailableRoles: async (): Promise<string[]> => {
    const response = await api.get('/User/AvailableRoles');
    return response.data;
  },

  register: async (data: any): Promise<any> => {
    const response = await api.post('/User/Register', data);
    return response.data;
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/User/${id}`);
  },

  toggleActive: async (id: string): Promise<any> => {
    const response = await api.post(`/User/${id}/ToggleActive`);
    return response.data;
  },

  getById: async (id: string): Promise<any> => {
    const response = await api.get(`/User/${id}`);
    return response.data;
  },

  getByEmail: async (email: string): Promise<any> => {
    const response = await api.get(`/User/ByEmail/${email}`);
    return response.data;
  },

  update: async (id: string, data: any): Promise<void> => {
    await api.put(`/User/${id}`, data);
  },

  updateProfilePhoto: async (id: string, profilePhotoUrl: string): Promise<void> => {
    await api.post(`/User/${id}/UpdateProfilePhoto`, {
      profilePhotoUrl
    });
  },

  changePassword: async (id: string, currentPassword: string, newPassword: string): Promise<void> => {
    await api.post(`/User/${id}/ChangePassword`, {
      currentPassword,
      newPassword
    });
  },

  resetPassword: async (id: string, newPassword: string): Promise<void> => {
    await api.post(`/User/${id}/ResetPassword`, {
      newPassword
    });
  },

  assignRole: async (id: string, role: string): Promise<void> => {
    await api.post(`/User/${id}/AssignRole`, { role });
  },

  removeRole: async (id: string, role: string): Promise<void> => {
    await api.delete(`/User/${id}/RemoveRole`, {
      data: { role }
    });
  },

  getUserRoles: async (id: string): Promise<string[]> => {
    const response = await api.get(`/User/${id}/Roles`);
    return response.data;
  },

  getStats: async (): Promise<any> => {
    const response = await api.get('/User/Stats');
    return response.data;
  },

  getProfile: async (): Promise<any> => {
    const response = await api.get('/User/Profile');
    return response.data;
  },

  updateRole: async (id: string, newRole: string): Promise<void> => {
    // Get current roles
    const currentRoles = await userAPI.getUserRoles(id);
    
    // Remove all current roles
    for (const role of currentRoles) {
      await api.delete(`/User/${id}/RemoveRole`, {
        data: { role }
      });
    }
    
    // Assign new role
    await api.post(`/User/${id}/AssignRole`, { role: newRole });
  },
};

// Sales Reports API
export const reportsAPI = {
  getSalesLast3Months: async (): Promise<any[]> => {
    const response = await api.get('/Reports/Sales/Last3Months');
    return response.data;
  },

  getSalesLast6Months: async (): Promise<any[]> => {
    const response = await api.get('/Reports/Sales/Last6Months');
    return response.data;
  },

  getSalesByDateRange: async (startDate?: string, endDate?: string): Promise<any[]> => {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    
    const response = await api.get(`/Reports/Sales?${params.toString()}`);
    return response.data;
  },

  exportSalesToExcel: async (startDate?: string, endDate?: string): Promise<Blob> => {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    
    const response = await api.get(`/Reports/Sales/ExportToExcel?${params.toString()}`, {
      responseType: 'blob'
    });
    return response.data;
  },
};

// Activity API
export const activityAPI = {
  getAll: async (limit?: number, activityType?: string, entityType?: string): Promise<any[]> => {
    const params = new URLSearchParams();
    if (limit) params.append('limit', limit.toString());
    if (activityType) params.append('activityType', activityType);
    if (entityType) params.append('entityType', entityType);
    
    const response = await api.get(`/Activity?${params.toString()}`);
    return response.data;
  },

  getRecent: async (limit: number = 50): Promise<any[]> => {
    const response = await api.get(`/Activity/Recent?limit=${limit}`);
    return response.data;
  },

  getByEntity: async (entityType: string, entityId: number): Promise<any[]> => {
    const response = await api.get(`/Activity/ByEntity/${entityType}/${entityId}`);
    return response.data;
  },

  getStats: async (): Promise<any> => {
    const response = await api.get('/Activity/Stats');
    return response.data;
  },
};

// Payment Receipts API
export const paymentReceiptAPI = {
  getByMember: async (membershipId: number): Promise<any[]> => {
    const response = await api.get(`/PaymentReceipt/member/${membershipId}`);
    return response.data;
  },

  getById: async (id: number): Promise<any> => {
    const response = await api.get(`/PaymentReceipt/${id}`);
    return response.data;
  },

  downloadReceipt: async (id: number): Promise<{ blob: Blob; format: 'pdf' | 'html' }> => {
    try {
      const response = await api.get(`/PaymentReceipt/${id}/download?t=${Date.now()}`, {
        responseType: 'blob',
        headers: {
          'Accept': 'application/pdf',
          'Cache-Control': 'no-cache'
        }
      });
      
      const blob = response.data as Blob;
      
      // Check content type from headers
      const contentType = (response.headers['content-type'] || response.headers['Content-Type'] || '').toLowerCase();
      
      // Check if it's PDF by magic number
      const firstBytes = await blob.slice(0, 4).arrayBuffer();
      const uint8Array = new Uint8Array(firstBytes);
      const pdfHeader = String.fromCharCode(...Array.from(uint8Array));
      
      if (pdfHeader === '%PDF') {
        return { blob, format: 'pdf' };
      }
      
      // Check if it's HTML
      if (contentType.includes('text/html')) {
        return { blob, format: 'html' };
      }
      
      // Try to detect HTML by content
      const contentPreview = await blob.slice(0, 200).text();
      const trimmedPreview = contentPreview.trim().toLowerCase();
      if (trimmedPreview.startsWith('<!doctype') || trimmedPreview.startsWith('<html')) {
        return { blob, format: 'html' };
      }
      
      // If not PDF and not HTML, try to parse as JSON error
      try {
        const errorText = await blob.text();
        const errorData = JSON.parse(errorText);
        throw new Error(errorData.message || errorData.title || 'Invalid file format');
      } catch (parseError) {
        throw new Error('Downloaded file format is not recognized. Please try again.');
      }
    } catch (error: any) {
      // Handle axios errors
      if (error.response) {
        const contentType = (error.response.headers?.['content-type'] || error.response.headers?.['Content-Type'] || '').toLowerCase();
        
        if (error.response.data instanceof Blob) {
          // Check if it's HTML
          if (contentType.includes('text/html')) {
            return { blob: error.response.data, format: 'html' };
          }
          
          // Try to detect HTML by content
          try {
            const text = await error.response.data.text();
            const trimmedText = text.trim().toLowerCase();
            if (trimmedText.startsWith('<!doctype') || trimmedText.startsWith('<html')) {
              return { blob: new Blob([text], { type: 'text/html' }), format: 'html' };
            }
            
            // Try to parse as JSON error
            const errorData = JSON.parse(text);
            throw new Error(errorData.message || errorData.title || 'Failed to download receipt');
          } catch (parseError) {
            throw new Error('Failed to download receipt. Please try again.');
          }
        }
      }
      throw error;
    }
  },

  viewReceiptHtml: async (id: number): Promise<string> => {
    const response = await api.get(`/PaymentReceipt/${id}/html?t=${Date.now()}`, {
      headers: {
        'Accept': 'text/html',
        'Cache-Control': 'no-cache'
      }
    });
    return response.data;
  },

  getAll: async (): Promise<any[]> => {
    const response = await api.get('/PaymentReceipt/all');
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/PaymentReceipt/${id}`);
  },

  resendEmail: async (id: number): Promise<any> => {
    const response = await api.post(`/PaymentReceipt/${id}/resend`);
    return response.data;
  },

  exportToExcel: async (): Promise<Blob> => {
    const response = await api.get('/PaymentReceipt/ExportToExcel', {
      responseType: 'blob'
    });
    return response.data;
  },
};

// Membership Freeze API
export const membershipFreezeAPI = {
  create: async (data: {
    membersMembershipId: number;
    freezeReason: string;
    freezeNotes: string;
    freezeStartDate: string;
    freezeEndDate: string;
    requestedBy?: string;
    documentUrl?: string;
  }): Promise<any> => {
    const response = await api.post('/MembershipFreeze', {
      ...data,
      freezeStartDate: new Date(data.freezeStartDate).toISOString(),
      freezeEndDate: new Date(data.freezeEndDate).toISOString(),
    });
    return response.data;
  },

  getById: async (id: number): Promise<any> => {
    const response = await api.get(`/MembershipFreeze/${id}`);
    return response.data;
  },

  getByMembership: async (membershipId: number): Promise<any[]> => {
    const response = await api.get(`/MembershipFreeze/ByMembership/${membershipId}`);
    return response.data;
  },

  getActiveFreeze: async (membershipId: number): Promise<any> => {
    const response = await api.get(`/MembershipFreeze/Active/ByMembership/${membershipId}`);
    return response.data;
  },

  unfreeze: async (id: number): Promise<any> => {
    const response = await api.post(`/MembershipFreeze/${id}/Unfreeze`);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/MembershipFreeze/${id}`);
  },
};

// Referral API
export const referralAPI = {
  getAll: async (): Promise<any[]> => {
    const response = await api.get('/Referral');
    return response.data;
  },

  getByReferrer: async (enquiryId: number): Promise<any[]> => {
    const response = await api.get(`/Referral/ByReferrer/${enquiryId}`);
    return response.data;
  },

  getLeaderboard: async (): Promise<any[]> => {
    const response = await api.get('/Referral/Leaderboard');
    return response.data;
  },

  create: async (data: {
    referrerEnquiryId: number;
    referredEnquiryId: number;
    rewardType?: string;
    rewardAmount?: number;
    notes?: string;
  }): Promise<any> => {
    const response = await api.post('/Referral', data);
    return response.data;
  },

  markConverted: async (id: number): Promise<any> => {
    const response = await api.post(`/Referral/${id}/MarkConverted`);
    return response.data;
  },

  giveReward: async (id: number): Promise<any> => {
    const response = await api.post(`/Referral/${id}/GiveReward`);
    return response.data;
  },

  getStats: async (): Promise<any> => {
    const response = await api.get('/Referral/Stats');
    return response.data;
  },
};

// Attendance API
export const attendanceAPI = {
  checkIn: async (data: {
    membersMembershipId: number;
    checkInMethod?: string;
    notes?: string;
  }): Promise<any> => {
    const response = await api.post('/Attendance/CheckIn', data);
    return response.data;
  },

  checkOut: async (id: number): Promise<any> => {
    const response = await api.post(`/Attendance/${id}/CheckOut`);
    return response.data;
  },

  getToday: async (): Promise<any[]> => {
    const response = await api.get('/Attendance/Today');
    return response.data;
  },

  getActive: async (): Promise<any> => {
    const response = await api.get('/Attendance/Active');
    return response.data;
  },

  getByMember: async (membershipId: number, days: number = 30): Promise<any[]> => {
    const response = await api.get(`/Attendance/ByMember/${membershipId}?days=${days}`);
    return response.data;
  },

  getStats: async (startDate?: string, endDate?: string): Promise<any> => {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    const response = await api.get(`/Attendance/Stats?${params.toString()}`);
    return response.data;
  },

  getReport: async (startDate?: string, endDate?: string): Promise<any> => {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    const response = await api.get(`/Attendance/Report?${params.toString()}`);
    return response.data;
  },
};

// Progress Tracking API
export const progressTrackingAPI = {
  create: async (data: {
    membersMembershipId: number;
    recordDate?: string;
    weight?: number;
    height?: number;
    bodyFatPercentage?: number;
    chest?: number;
    waist?: number;
    hips?: number;
    biceps?: number;
    thighs?: number;
    calves?: number;
    frontPhotoUrl?: string;
    sidePhotoUrl?: string;
    backPhotoUrl?: string;
    goals?: string;
    notes?: string;
  }): Promise<any> => {
    const response = await api.post('/ProgressTracking', {
      ...data,
      recordDate: data.recordDate ? new Date(data.recordDate).toISOString() : undefined,
    });
    return response.data;
  },

  getById: async (id: number): Promise<any> => {
    const response = await api.get(`/ProgressTracking/${id}`);
    return response.data;
  },

  getByMember: async (membershipId: number): Promise<any[]> => {
    const response = await api.get(`/ProgressTracking/ByMember/${membershipId}`);
    return response.data;
  },

  getLatest: async (membershipId: number): Promise<any> => {
    const response = await api.get(`/ProgressTracking/Latest/ByMember/${membershipId}`);
    return response.data;
  },

  getComparison: async (membershipId: number): Promise<any> => {
    const response = await api.get(`/ProgressTracking/Comparison/ByMember/${membershipId}`);
    return response.data;
  },

  update: async (id: number, data: {
    weight?: number;
    height?: number;
    bodyFatPercentage?: number;
    chest?: number;
    waist?: number;
    hips?: number;
    biceps?: number;
    thighs?: number;
    calves?: number;
    goals?: string;
    notes?: string;
  }): Promise<void> => {
    await api.put(`/ProgressTracking/${id}`, data);
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/ProgressTracking/${id}`);
  },
};

// Feedback API
export const feedbackAPI = {
  submit: async (data: {
    membersMembershipId?: number;
    enquiryId?: number;
    memberName?: string;
    email?: string;
    feedbackType?: string;
    category?: string;
    rating?: number;
    message: string;
    isAnonymous?: boolean;
  }): Promise<any> => {
    const response = await api.post('/Feedback', data);
    return response.data;
  },

  getAll: async (status?: string): Promise<any[]> => {
    const params = status ? `?status=${status}` : '';
    const response = await api.get(`/Feedback${params}`);
    return response.data;
  },

  getById: async (id: number): Promise<any> => {
    const response = await api.get(`/Feedback/${id}`);
    return response.data;
  },

  respond: async (id: number, response_text: string, status?: string): Promise<any> => {
    const response = await api.post(`/Feedback/${id}/Respond`, {
      response: response_text,
      status: status || 'Reviewed'
    });
    return response.data;
  },

  updateStatus: async (id: number, status: string): Promise<void> => {
    await api.put(`/Feedback/${id}/Status`, { status });
  },

  getStats: async (): Promise<any> => {
    const response = await api.get('/Feedback/Stats');
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/Feedback/${id}`);
  },
};

// Bulk Notification API
export const bulkNotificationAPI = {
  create: async (data: {
    title: string;
    message: string;
    type?: string;
    targetAudience?: string;
    targetMembershipIds?: number[];
    scheduledFor?: string;
    sendNow?: boolean;
  }): Promise<any> => {
    const response = await api.post('/BulkNotification', {
      ...data,
      scheduledFor: data.scheduledFor ? new Date(data.scheduledFor).toISOString() : undefined,
    });
    return response.data;
  },

  getById: async (id: number): Promise<any> => {
    const response = await api.get(`/BulkNotification/${id}`);
    return response.data;
  },

  getAll: async (): Promise<any[]> => {
    const response = await api.get('/BulkNotification');
    return response.data;
  },

  send: async (id: number): Promise<any> => {
    const response = await api.post(`/BulkNotification/${id}/Send`);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/BulkNotification/${id}`);
  },
};

// PT Session API
export const ptSessionAPI = {
  create: async (data: {
    membersMembershipId: number;
    trainerId: string;
    trainerName: string;
    sessionDate: string;
    startTime: string;
    endTime: string;
    durationMinutes: number;
    sessionType?: string;
    focusAreas?: string;
    exercises?: string;
    notes?: string;
  }): Promise<any> => {
    const response = await api.post('/PTSession', {
      ...data,
      sessionDate: new Date(data.sessionDate).toISOString(),
    });
    return response.data;
  },

  getById: async (id: number): Promise<any> => {
    const response = await api.get(`/PTSession/${id}`);
    return response.data;
  },

  getByMember: async (membershipId: number): Promise<any[]> => {
    const response = await api.get(`/PTSession/ByMember/${membershipId}`);
    return response.data;
  },

  getByTrainer: async (trainerId: string, date?: string): Promise<any[]> => {
    const params = date ? `?date=${date}` : '';
    const response = await api.get(`/PTSession/ByTrainer/${trainerId}${params}`);
    return response.data;
  },

  complete: async (id: number, exercises?: string, notes?: string): Promise<void> => {
    await api.put(`/PTSession/${id}/Complete`, { exercises, notes });
  },

  cancel: async (id: number): Promise<void> => {
    await api.put(`/PTSession/${id}/Cancel`);
  },

  getTrainerStats: async (trainerId: string, days: number = 30): Promise<any> => {
    const response = await api.get(`/PTSession/Stats/ByTrainer/${trainerId}?days=${days}`);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/PTSession/${id}`);
  },
};

// Gym Class API
export const gymClassAPI = {
  create: async (data: {
    className: string;
    description?: string;
    classType?: string;
    instructorId?: string;
    instructorName?: string;
    scheduleDay: string;
    startTime: string;
    endTime: string;
    durationMinutes: number;
    capacity: number;
    isRecurring?: boolean;
  }): Promise<any> => {
    const response = await api.post('/GymClass', data);
    return response.data;
  },

  getById: async (id: number): Promise<any> => {
    const response = await api.get(`/GymClass/${id}`);
    return response.data;
  },

  getAll: async (activeOnly: boolean = true): Promise<any[]> => {
    const response = await api.get(`/GymClass?activeOnly=${activeOnly}`);
    return response.data;
  },

  enroll: async (id: number, membershipId: number): Promise<any> => {
    const response = await api.post(`/GymClass/${id}/Enroll`, {
      membersMembershipId: membershipId
    });
    return response.data;
  },

  unenroll: async (id: number, membershipId: number): Promise<any> => {
    const response = await api.post(`/GymClass/${id}/Unenroll`, {
      membersMembershipId: membershipId
    });
    return response.data;
  },

  getByMember: async (membershipId: number): Promise<any[]> => {
    const response = await api.get(`/GymClass/ByMember/${membershipId}`);
    return response.data;
  },

  update: async (id: number, data: {
    className?: string;
    description?: string;
    capacity?: number;
    isActive?: boolean;
  }): Promise<void> => {
    await api.put(`/GymClass/${id}`, data);
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/GymClass/${id}`);
  },
};

// Analytics API
export const analyticsAPI = {
  getOverview: async (days: number = 30): Promise<any> => {
    const response = await api.get(`/Analytics/Overview?days=${days}`);
    return response.data;
  },

  getRetention: async (months: number = 6): Promise<any> => {
    const response = await api.get(`/Analytics/Retention?months=${months}`);
    return response.data;
  },

  getLTV: async (): Promise<any> => {
    const response = await api.get('/Analytics/LTV');
    return response.data;
  },

  getRevenue: async (days: number = 90): Promise<any> => {
    const response = await api.get(`/Analytics/Revenue?days=${days}`);
    return response.data;
  },

  getMemberGrowth: async (months: number = 12): Promise<any> => {
    const response = await api.get(`/Analytics/MemberGrowth?months=${months}`);
    return response.data;
  },
};

export default api;
